import SDK from 'bookthatapp-sdk';
import qs from 'qs';
import dayjs from '../../dayjs/dayjs_timezone.js';
import { SHORT_DATE_FORMAT } from '../constants';

const sdk = SDK.init(window.bta.apiToken, { apiBaseUrl: window.bta.apiBaseUrl });
const isUsedSdk = () => !!window.bta.apiToken; // sdk is used for development env (without proxy)
const isVerbose = () => !!window.bta.isDemo;
const proxyBaseUrl = () => (window.bta.proxyBaseUrl);

/**
 * Fetch wrapper that mimics axios behavior
 * - Automatically throws on HTTP errors (4xx, 5xx)
 * - Returns response in axios-like format: { data }
 */
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);

  if (!response.ok) {
    const error = new Error(`HTTP Error ${response.status}`);
    let data = null;

    try {
      data = await response.json();
    } catch (jsonError) {
      // If response body is not JSON, leave data as null
      console.error('Failed to parse response body as JSON:', jsonError);
    }

    error.response = {
      status: response.status,
      statusText: response.statusText,
      data,
    };
    throw error;
  }

  const data = await response.json();
  return { data };
};

/**
 * Recursively normalize params so Date/Dayjs objects stringify to ISO.
 */
const normalizeParams = (params) => {
  if (Array.isArray(params)) return params.map(normalizeParams);
  if (params instanceof Date || dayjs.isDayjs(params)) return params.toISOString();
  if (params && typeof params === 'object') {
    return Object.entries(params).reduce((acc, [key, value]) => {
      acc[key] = normalizeParams(value);
      return acc;
    }, {});
  }
  return params;
};

/**
 * Helper to build URL with query parameters using qs for proper nested serialization
 */
const buildUrl = (baseUrl, params) => {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }

  const normalizedParams = normalizeParams(params);
  const queryString = qs.stringify(normalizedParams, { arrayFormat: 'brackets' });
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const convertFramesToShopTimezone = (frames, timeZone) => {
  // frames returned from server is in UTC
  // we will re-arrange them into shop timezone
  const clientFrames = {};
  let i = 0;

  frames.forEach((f) => {
    f.timeslots.forEach((t) => {
      const timeslotDate = dayjs(t.start)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT);
      if (!clientFrames[timeslotDate]) {
        clientFrames[timeslotDate] = {
          id: i,
          year: dayjs(t.start)
            .tz(timeZone)
            .format('YYYY'),
          month: dayjs(t.start)
            .tz(timeZone)
            .format('MM'),
          date: dayjs(t.start)
            .tz(timeZone)
            .format(SHORT_DATE_FORMAT),
          timeslots: [],
        };
        i++;
      }

      clientFrames[timeslotDate].timeslots.push({
        ...t,
        date: dayjs(t.start)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT),
        start: t.start,
        finish: t.finish,
      });
    });
  });

  return Object.values(clientFrames);
};

export const flattenFramesToShopTimezone = (frames, timeZone) => {
  const arr = [];

  frames.forEach((frame) => {
    frame.timeslots.forEach((timeslot) => {
      arr.push({
        ...timeslot,
        startStr: dayjs(timeslot.start).format('YYYY-MM-DD HH:mm:ss Z'),
        start: dayjs(timeslot.start)
          .tz(timeZone)
          .toDate(),
        finishStr: dayjs(timeslot.finish).format('YYYY-MM-DD HH:mm:ss Z'),
        finish: dayjs(timeslot.finish)
          .tz(timeZone)
          .toDate(),
      });
    });
  });

  return arr;
};

const buildFrameData = (data) => {
  const { timeZone } = window.bta;

  return {
    ...data,
    frames: convertFramesToShopTimezone(data.frames, timeZone),
    flattenTimeSlots: flattenFramesToShopTimezone(data.frames, timeZone),
    lastOccurrence: data.last_occurrence
      ? dayjs(data.last_occurrence)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT)
      : null,
    nextOccurrence: data.next_occurrence
      ? dayjs(data.next_occurrence)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT)
      : null,
  };
};

export const getFrames = ({
  external_id, start, finish, location_id, resource_id, is_events_list,
}) => {
  const params = {
    external_id,
    start,
    finish,
    location_id,
    resource_id,
    is_events_list,
  };

  if (isUsedSdk()) {
    return sdk.getFrames(params)
      .then(({ data }) => Promise.resolve(buildFrameData(data)));
  }
  const url = buildUrl(`${proxyBaseUrl()}/api/v1/frames`, {
    ...params,
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url, {
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve(buildFrameData(data)));
};

export const getCourseFrames = ({
  external_id, start, finish, location_id, resource_id,
}) => {
  const params = {
    external_id,
    start,
    finish,
    location_id,
    resource_id,
  };

  if (isUsedSdk()) {
    return sdk.getCourseFrames(params)
      .then(({ data }) => Promise.resolve(buildFrameData(data)));
  }
  const url = buildUrl(`${proxyBaseUrl()}/api/v1/courses`, {
    ...params,
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url, {
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve(buildFrameData(data)));
};

export const getBlocks = ({
  // eslint-disable-next-line camelcase
  external_id, interval, finish, location_ids, resource_ids, start, with_finish_availability,
}) => {
  const params = {
    external_id,
    interval,
    start: dayjs(start).format(SHORT_DATE_FORMAT),
    finish: dayjs(finish).format(SHORT_DATE_FORMAT),
    location_ids,
    resource_ids,
    with_finish_availability,
  };

  if (isUsedSdk()) {
    return sdk
      .getBlocks(params)
      .then(({ data }) => Promise.resolve({ ...data }));
  }
  const url = buildUrl(`${proxyBaseUrl()}/api/v1/blocks`, {
    ...params,
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url, {
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve({ ...data }));
};

export const createBooking = (bookingParam) => {
  const params = {
    cart_token: window.xprops.cartToken,
    ...bookingParam,
    shop: window.bta ? window.bta.shop : null,
  };

  if (isUsedSdk()) {
    return sdk.createBooking(params);
  }

  return fetchJSON(`${proxyBaseUrl()}/api/v1/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: qs.stringify(params),
  });
};

export const rescheduleBooking = (bookingToken, params) => {
  return fetchJSON(`${proxyBaseUrl()}/bookings/reschedule/${bookingToken}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
  });
};

export const createReservation = (items, check_waitlist, retry, check_expires) => {
  const params = {
    cart_token: window.xprops.cartToken,
    items,
    retry,
    check_waitlist,
    check_expires,
    shop: window.bta ? window.bta.shop : null,
  };

  if (isUsedSdk()) {
    return sdk.createReservation(params);
  }

  return fetchJSON(`${proxyBaseUrl()}/api/v1/reservations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: qs.stringify(params),
  });
};

export const confirmAvailability = (items, check_waitlist, retry, check_expires) => {
  const params = {
    cart_token: window.xprops.cartToken,
    items,
    retry,
    check_waitlist,
    check_expires,
    shop: window.bta ? window.bta.shop : null,
  };

  if (isUsedSdk()) {
    return sdk.createReservation(params);
  }

  return fetchJSON(`${proxyBaseUrl()}/api/v1/confirm_availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: qs.stringify(params),
  });
};

export const addToWaitlist = (productId, params) => {
  return fetchJSON(`${proxyBaseUrl()}/api/v1/products/${productId}/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
  });
};

export const availabilitySearch = (params) => {
  const url = buildUrl(`${proxyBaseUrl()}/api/v1/products/search`, {
    options: params,
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url).then(({ data }) => Promise.resolve({ ...data }));
};

export const getLocations = (params) => {
  const url = buildUrl(`${proxyBaseUrl()}/api/v1/locations`, {
    ...params,
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url).then(({ data }) => Promise.resolve({ ...data }));
};

export const getWidgetProducts = (widgetId) => {
  const url = buildUrl(`${proxyBaseUrl()}/widgets/${widgetId}/products`, {
    shop: window.bta ? window.bta.shop : null,
  });
  return fetchJSON(url);
};

export const getProducts = (customParams) => {
  if (isUsedSdk()) {
    return sdk.getProducts(customParams).then((response) => response.data);
  }
  const params = {
    ...customParams,
    shop: window.bta ? window.bta.shop : null,
  };

  const url = buildUrl(`${proxyBaseUrl()}/api/v1/products`, params);
  return fetchJSON(url).then((response) => response.data);
};

export const getSchedules = (params) => {
  if (window.bta && window.bta.appUrl) {
    const { appUrl } = window.bta;
    const apiUrl = buildUrl(`${appUrl}/availability/schedule.json`, params);
    return fetchJSON(apiUrl).then((response) => (response.data));
  }
  return Promise.reject(new Error('getSchedules - no window.xprops...'));
};

export const getCalendarEvents = (params) => {
  if (window.bta && window.bta.appUrl) {
    const { appUrl } = window.bta;
    const apiUrl = buildUrl(`${appUrl}/calendar/events.json`, params);
    return fetchJSON(apiUrl).then((response) => (response.data));
  }
  return Promise.reject(new Error('getCalendarEvents - no window.xprops...'));
};

export const getUpcomingEventsProducts = (options) => {
  const { showBookedOut, showRepeatOccurrences } = options || {};

  const url = buildUrl(`${proxyBaseUrl()}/upcoming_events_products`, {
    start: dayjs().utc().format('YYYY-MM-DDTHH:mm'),
    finish: dayjs().utc().add(5, 'month').format('YYYY-MM-DDTHH:mm'),
    shop: window.bta ? window.bta.shop : null,
    show_booked_out: showBookedOut || false,
    show_repeat_occurrences: showRepeatOccurrences || false,
  });

  return fetchJSON(url).then((response) => (response.data));
};

export default {
  availabilitySearch,
  createReservation,
  getBlocks,
  getCourseFrames,
  getFrames,
  getProducts,
  getSchedules,
};
